/**
 * One lazily-created AudioContext for every app sound (ringtone, message blip).
 *
 * Synthesised tones instead of audio files: zero network bytes, zero decode
 * cost, and no <audio> element per sound. The context is only created after a
 * caller asks for a sound, so a tab that never rings never allocates it.
 */
let context: AudioContext | null = null;

type AudioContextCtor = new () => AudioContext;

function getCtor(): AudioContextCtor | null {
  const scope = window as typeof window & { webkitAudioContext?: AudioContextCtor };
  return scope.AudioContext ?? scope.webkitAudioContext ?? null;
}

export function getAudioContext(): AudioContext | null {
  if (context) return context;
  const Ctor = getCtor();
  if (!Ctor) return null;
  try {
    context = new Ctor();
  } catch {
    return null;
  }
  return context;
}

/** Browsers suspend the context until a gesture; resume is best-effort. */
export function resumeAudio(): void {
  const ctx = getAudioContext();
  if (ctx && ctx.state === 'suspended') void ctx.resume().catch(() => undefined);
}

export interface ToneOptions {
  frequency: number;
  /** Seconds from now. */
  startAt: number;
  duration: number;
  gain?: number;
  type?: OscillatorType;
}

/** Schedules one enveloped tone. Nodes are disposed by the browser on `ended`. */
export function playTone({ frequency, startAt, duration, gain = 0.08, type = 'sine' }: ToneOptions): void {
  const ctx = getAudioContext();
  if (!ctx) return;

  const start = ctx.currentTime + startAt;
  const oscillator = ctx.createOscillator();
  const envelope = ctx.createGain();

  oscillator.type = type;
  oscillator.frequency.setValueAtTime(frequency, start);

  envelope.gain.setValueAtTime(0, start);
  envelope.gain.linearRampToValueAtTime(gain, start + 0.02);
  envelope.gain.setValueAtTime(gain, start + duration - 0.04);
  envelope.gain.linearRampToValueAtTime(0, start + duration);

  oscillator.connect(envelope).connect(ctx.destination);
  oscillator.start(start);
  oscillator.stop(start + duration + 0.02);
}
